"use client";

import NodeMaterialSystem from './NodeMaterialSystem.js';

export default class NodeRenderer {
  constructor(ctx) {
    this.ctx = ctx;
    this.materialSystem = new NodeMaterialSystem();
  }

  draw({ vibes, links, camera, state, atmosphere, time, clusterAssignments, territories }) {
    const selectedIds = new Set(state.selectedVibeIds);

    this.drawLinks(links, camera, atmosphere);
    this.drawNodes(vibes, camera, selectedIds, state, clusterAssignments, atmosphere, time, territories);
  }

  drawLinks(links, camera, atmosphere) {
    this.ctx.save();
    this.ctx.lineCap = 'round';

    links.forEach(link => {
      const start = camera.worldToScreen(link.a.x, link.a.y);
      const end = camera.worldToScreen(link.b.x, link.b.y);
      const alpha = 0.06 + link.strength * 0.24 + atmosphere.glow * 0.08;

      this.ctx.beginPath();
      this.ctx.moveTo(start.x, start.y);
      this.ctx.lineTo(end.x, end.y);
      this.ctx.lineWidth = 0.8 + link.strength * 1.8 * camera.zoom;
      this.ctx.strokeStyle = `rgba(142, 180, 255, ${Math.min(alpha, 0.55)})`;
      this.ctx.stroke();
    });

    this.ctx.restore();
  }

  drawNodes(vibes, camera, selectedIds, state, clusterAssignments, atmosphere, time, territories) {
    const safeTerritories = Array.isArray(territories) ? territories : [];

    vibes.forEach(vibe => {
      const screen = camera.worldToScreen(vibe.x, vibe.y);
      const isHovered = state.hoveredVibeId === vibe.id;
      const isSelected = selectedIds.has(vibe.id);

      const clusterId = clusterAssignments?.get(vibe.id);
      const territory = safeTerritories.find(t => t.id === clusterId) || null;

      const material = this.materialSystem.getMaterial(vibe, territory, time, state);

      const radius = vibe.radius * camera.zoom * material.radiusMultiplier;
      const glowRadius = radius * (1.5 + material.glow);
      const paletteColor = vibe.colorPalette?.[0] || '#c9d5ff';

      this.ctx.save();

      // 1. Territory Influence
      if (material.territoryColor !== 'rgba(0,0,0,0)') {
        this.ctx.beginPath();
        this.ctx.arc(screen.x, screen.y, radius * 3, 0, Math.PI * 2);
        this.ctx.fillStyle = material.territoryColor;
        this.ctx.fill();
      }

      // 2. Atmospheric Halo
      this.ctx.beginPath();
      this.ctx.arc(screen.x, screen.y, glowRadius, 0, Math.PI * 2);
      const gradient = this.ctx.createRadialGradient(
        screen.x, screen.y, radius * 0.5,
        screen.x, screen.y, glowRadius
      );
      gradient.addColorStop(0, this.hexToRgba(paletteColor, material.glow * 0.4));
      gradient.addColorStop(1, 'rgba(0, 0, 0, 0)');
      this.ctx.fillStyle = gradient;
      this.ctx.fill();

      // 3. Core Particle
      this.ctx.beginPath();
      this.ctx.arc(screen.x, screen.y, radius, 0, Math.PI * 2);
      this.ctx.fillStyle = material.color;
      this.ctx.shadowColor = paletteColor;
      this.ctx.shadowBlur = isHovered || isSelected ? 15 : 5;
      this.ctx.fill();

      // 4. Selection/Hover Ring
      if (isHovered || isSelected) {
        this.ctx.beginPath();
        this.ctx.arc(screen.x, screen.y, radius + 4, 0, Math.PI * 2);
        this.ctx.lineWidth = isSelected ? 2.5 : 1.5;
        this.ctx.strokeStyle = isSelected ? '#fffbe6' : 'rgba(255, 255, 255, 0.5)';
        this.ctx.stroke();
      }

      this.ctx.restore();
    });
  }

  hexToRgba(hex, alpha) {
    const normalized = hex.replace('#', '');
    const value = normalized.length === 3
      ? normalized.split('').map(char => char.repeat(2)).join('')
      : normalized;
    const int = Number.parseInt(value, 16);
    return `rgba(${(int >> 16) & 255}, ${(int >> 8) & 255}, ${int & 255}, ${alpha})`;
  }
}